import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type {
  ActivityDto,
  BrandAssetDto,
  ClientDto,
  ClientStatus,
  ClientSummaryDto,
  ClientUserDto,
  ContactDto,
  FileDto,
  InvitationDto,
} from '@cw/shared';
import { api } from './api';
import { useCan } from './session';

export const clientKeys = {
  all: ['clients'] as const,
  list: (q: string, status: string) => ['clients', 'list', q, status] as const,
  detail: (id: string) => ['clients', id] as const,
  contacts: (id: string) => ['clients', id, 'contacts'] as const,
  brand: (id: string) => ['clients', id, 'brand'] as const,
  files: (id: string) => ['clients', id, 'files'] as const,
  activity: (id: string) => ['clients', id, 'activity'] as const,
  users: (id: string) => ['clients', id, 'users'] as const,
  invitations: (id: string) => ['clients', id, 'invitations'] as const,
};

export function useClients(q = '', status: ClientStatus | 'all' = 'all') {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (status !== 'all') params.set('status', status);
  return useQuery({
    queryKey: clientKeys.list(q, status),
    queryFn: () => api.get<ClientSummaryDto[]>(`/clients?${params}`),
    enabled: useCan('clients:read'),
  });
}

export function useClient(id: string) {
  return useQuery({ queryKey: clientKeys.detail(id), queryFn: () => api.get<ClientDto>(`/clients/${id}`) });
}

export function useContacts(id: string) {
  return useQuery({
    queryKey: clientKeys.contacts(id),
    queryFn: () => api.get<ContactDto[]>(`/clients/${id}/contacts`),
    enabled: useCan('clients:contacts'),
  });
}

export function useBrandAssets(id: string) {
  return useQuery({
    queryKey: clientKeys.brand(id),
    queryFn: () => api.get<BrandAssetDto[]>(`/clients/${id}/brand-assets`),
  });
}

export function useClientFiles(id: string) {
  return useQuery({
    queryKey: clientKeys.files(id),
    queryFn: () => api.get<FileDto[]>(`/files?clientId=${id}&purpose=client_file`),
    enabled: useCan('files:read'),
  });
}

export function useClientActivity(id: string) {
  return useInfiniteQuery({
    queryKey: clientKeys.activity(id),
    queryFn: ({ pageParam }) =>
      api.get<{ items: ActivityDto[]; nextBefore: string | null }>(
        `/clients/${id}/activity${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    enabled: useCan('activity:read'),
  });
}

export function useClientUsers(id: string) {
  const can = useCan('clients:invite');
  const users = useQuery({
    queryKey: clientKeys.users(id),
    queryFn: () => api.get<ClientUserDto[]>(`/clients/${id}/users`),
    enabled: can,
  });
  const invitations = useQuery({
    queryKey: clientKeys.invitations(id),
    queryFn: () => api.get<InvitationDto[]>(`/clients/${id}/invitations`),
    enabled: can,
  });
  return { users, invitations };
}
